import { SizingStore } from './sizing-store';
import { Article } from '../../models/sizing-data';
import { sendEvent } from '../../services/event-tracking';
import { isDbMigrated } from '../../lib/rest-feature-flag';
import presizeBackendRest from '../../services/presize-backend-rest';
import { runInAction } from 'mobx';

export class UpdateArticlesAction {
  async updateArticles(
    this: SizingStore,
    _selectedGroups: Array<string>,
    selectedArticles: Array<Article>,
    modifier: Partial<Article>
  ): Promise<void> {
    if (isDbMigrated()) {
      await this.updateArticlesRest(selectedArticles, modifier);
      return;
    }

    await this.updateArticlesGraphql(selectedArticles, modifier);
  }

  private async updateArticlesGraphql(
    this: SizingStore,
    selectedArticles: Array<Article>,
    modifier: Partial<Article>
  ): Promise<void> {
    const selectedArticlesByRefId = new Map(selectedArticles.map((article) => [article.referenceId, article]));

    this.articles = this.articles.map((article) => {
      const selectedArticle = selectedArticlesByRefId.get(article.referenceId);
      if (!selectedArticle) return article;
      return { ...article, ...modifier };
    });

    await this.groupArticles();

    for (const article of selectedArticles) {
      const group = this.groupByArticleRefId[article.referenceId];
      const updatedArticle = group.articles.find((a) => a.referenceId === article.referenceId) as Article;

      if ('sizingNote' in modifier) {
        sendEvent(group, 'addSizeNote', [updatedArticle], this.additionalData());
      } else if ('sizeStructure' in modifier) {
        sendEvent(group, 'adjustSizeStructureGrouping', [updatedArticle], this.additionalData());
      } else if ('silo' in modifier) {
        sendEvent(group, 'updateSilo', [updatedArticle], this.additionalData());
      }
    }
  }

  private async updateArticlesRest(
    this: SizingStore,
    selectedArticles: Array<Article>,
    modifier: Partial<Article>
  ): Promise<void> {
    const articleReferenceIds = selectedArticles.map((article) => article.referenceId);
    const patchFields = this.toRestPatchFields(modifier);
    const normalizedModifier = this.toNormalizedModifier(modifier);
    if (!patchFields) {
      // Keep behaviour unchanged for unsupported modifiers.
      await this.updateArticlesGraphql(selectedArticles, modifier);
      return;
    }

    await presizeBackendRest.updateArticles(
      this.referenceId,
      {
        article_reference_ids: articleReferenceIds,
        ...patchFields,
      },
      undefined,
      'application/merge-patch+json'
    );

    const selectedArticlesByRefId = new Set(articleReferenceIds);
    runInAction(() => {
      this.articles = this.articles.map((article) => {
        if (!selectedArticlesByRefId.has(article.referenceId)) return article;
        return { ...article, ...normalizedModifier };
      });
    });

    try {
      await this.regroupAndReloadSizing();
    } catch {
      throw new Error('Changes were saved, but sizing data could not be reloaded. Please reload the page.');
    }

    for (const article of selectedArticles) {
      const group = this.groupByArticleRefId[article.referenceId];
      const updatedArticle = group.articles.find((a) => a.referenceId === article.referenceId) as Article;

      if ('sizingNote' in modifier) {
        sendEvent(group, 'addSizeNote', [updatedArticle], this.additionalData());
      } else if ('sizeStructure' in modifier) {
        sendEvent(group, 'adjustSizeStructureGrouping', [updatedArticle], this.additionalData());
      } else if ('silo' in modifier) {
        sendEvent(group, 'updateSilo', [updatedArticle], this.additionalData());
      }
    }
  }

  private toRestPatchFields(
    this: SizingStore,
    modifier: Partial<Article>
  ): { silo?: string | null; sizing_note?: string | null; size_structure?: string | null } | null {
    const normalizeNullableField = (value: string | null | undefined): string | null =>
      value === '' || value == null ? null : value;

    if ('silo' in modifier) {
      return { silo: normalizeNullableField(modifier.silo) };
    }
    if ('sizingNote' in modifier) {
      return { sizing_note: normalizeNullableField(modifier.sizingNote) };
    }
    if ('sizeStructure' in modifier) {
      return { size_structure: normalizeNullableField(modifier.sizeStructure) };
    }
    return null;
  }

  private toNormalizedModifier(this: SizingStore, modifier: Partial<Article>): Partial<Article> {
    const normalizeNullableField = (value: string | null | undefined): string | null | undefined =>
      value === '' || value == null ? null : value;

    if ('silo' in modifier) {
      return { ...modifier, silo: normalizeNullableField(modifier.silo) };
    }
    if ('sizingNote' in modifier) {
      return { ...modifier, sizingNote: normalizeNullableField(modifier.sizingNote) };
    }
    if ('sizeStructure' in modifier) {
      return { ...modifier, sizeStructure: normalizeNullableField(modifier.sizeStructure) };
    }

    return modifier;
  }

  clearMismatches(this: SizingStore): void {
    this.mismatchedArticles = [];
    this.mismatchedGroups = {};
  }
}
